import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Tag, Plus, RefreshCw } from "lucide-react";

// ── Changelog data (mirrors CHANGELOG.md) ──────────────────
interface ChangelogEntry {
    version: string;
    date: string;
    added?: string[];
    changed?: string[];
    fixed?: string[];
}

const ENTRIES: ChangelogEntry[] = [
    {
        version: "Unreleased",
        date: "2026-02-21",
        added: [
            "Markdown Preview tool — split editor with live GFM rendering",
            "Cron Expression Parser — human-readable descriptions + next 5 runs",
            "Box Shadow Generator — visual sliders, multi-layer, CSS output",
            "API Tester — lightweight Postman with custom headers and JSON body",
            "Search bar and category filters on the Tools index page",
            "Dynamic meta tags for every route (SEO)",
            "sitemap.xml and robots.txt for search engine indexing",
            "Changelog page",
        ],
        changed: [
            "Tools index shows 17 tools (up from 13)",
            "Download buttons restyled with violet CTA and proper Apple logo",
        ],
    },
    {
        version: "0.3.0",
        date: "2026-02-14",
        added: [
            "JWT Decoder — decode and inspect JSON Web Tokens",
            "JWT Encoder — create and sign JWTs with HS256/HS384/HS512",
            "JSON Formatter — prettify, minify, and validate JSON",
            "Base64 Encoder/Decoder",
            "URL Encoder/Decoder",
            "Hash Generator — SHA-256, SHA-512 and more",
            "UUID & ULID Generator with bulk generation",
            "Color Converter — Hex, RGB, HSL with visual picker",
            "Regex Tester — real-time matching with flags",
            "Diff Checker — side-by-side text comparison",
            "AI Model Comparison — LLM pricing and specs",
            "LLM Token Counter — token counts for GPT/Claude/Gemini",
            "Favicon updated to Geist SemiBold B glyph",
        ],
        changed: [
            "Download button detects Apple Silicon vs Intel",
            "Download flow checks availability, proper filename, notify-me fallback",
        ],
    },
    {
        version: "0.2.0",
        date: "2026-02-14",
        added: [
            "Email-gated download flow on the landing page",
            "Ballah Data API integration for email capture",
            "Real Apple logo SVG in download button",
            "Apple Silicon vs Intel architecture detection via WebGL",
        ],
        changed: ["Landing page copy and layout refinements"],
    },
    {
        version: "0.1.0",
        date: "2026-02-14",
        added: [
            "Initial landing page with hero, product screenshots, and features",
            "Currently in beta badge",
            "Feature cards: Smarter, Faster, Cheaper",
            "Tech stack ticker: Electrobun, Bun, 14MB, Native webview, TypeScript, BYOK",
            "Email capture with bottom CTA section",
            "Bun-based build pipeline with Tailwind CSS",
            "Dark theme design system",
        ],
    },
];

// ── Component ──────────────────────────────────────────────
export function Changelog() {
    useEffect(() => {
        document.title = "Changelog — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta)
            meta.setAttribute(
                "content",
                "See what's new in Ballah Code. Full changelog with every feature, improvement, and fix.",
            );
    }, []);

    return (
        <div className="min-h-screen bg-[#0a0a0b] text-zinc-200">
            {/* Nav */}
            <nav className="sticky top-0 z-30 bg-[#0a0a0b]/80 backdrop-blur-md border-b border-white/[0.04] flex items-center justify-between px-8 py-[18px]">
                <Link
                    to="/"
                    className="text-sm font-semibold text-zinc-200 hover:text-white transition-colors tracking-[-0.03em]"
                >
                    ballah code
                </Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">
                        tools
                    </Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">
                        home
                    </Link>
                </div>
            </nav>

            {/* Content */}
            <main className="max-w-3xl mx-auto px-6 pt-12 pb-20">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-6">
                    <Link to="/" className="hover:text-zinc-400 transition-colors flex items-center gap-1">
                        <ArrowLeft size={12} />
                        Home
                    </Link>
                    <span>/</span>
                    <span className="text-zinc-400">Changelog</span>
                </div>

                <h1 className="text-3xl font-bold tracking-tight mb-2">Changelog</h1>
                <p className="text-zinc-500 text-[15px] mb-12">
                    All notable changes to Ballah Code, newest first.
                </p>

                {/* Timeline */}
                <div className="space-y-12">
                    {ENTRIES.map((entry, i) => (
                        <article
                            key={entry.version}
                            className="relative pl-7 border-l border-white/[0.06]"
                        >
                            {/* Dot */}
                            <div
                                className={`absolute left-[-5px] top-[6px] w-[10px] h-[10px] rounded-full border-2 ${i === 0
                                    ? "border-violet-500 bg-violet-500/20"
                                    : "border-zinc-700 bg-[#0a0a0b]"
                                    }`}
                            />

                            {/* Header */}
                            <div className="flex items-center gap-3 mb-4">
                                <span
                                    className={`text-[13px] font-mono font-semibold px-2 py-0.5 rounded ${i === 0
                                        ? "bg-violet-500/10 text-violet-400"
                                        : "bg-white/[0.04] text-zinc-400"
                                        }`}
                                >
                                    {entry.version}
                                </span>
                                <span className="text-[13px] text-zinc-600">
                                    {new Date(entry.date + "T00:00:00").toLocaleDateString(
                                        "en-US",
                                        { year: "numeric", month: "long", day: "numeric" },
                                    )}
                                </span>
                            </div>

                            {/* Added */}
                            {entry.added && entry.added.length > 0 && (
                                <div className="mb-4">
                                    <h3 className="text-[11px] font-semibold uppercase tracking-widest text-emerald-500/80 mb-2 flex items-center gap-1.5">
                                        <Plus size={11} /> Added
                                    </h3>
                                    <ul className="space-y-1.5">
                                        {entry.added.map((item, j) => (
                                            <li
                                                key={j}
                                                className="text-[14px] text-zinc-400 leading-relaxed"
                                            >
                                                <span className="text-zinc-600 mr-2">•</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Changed */}
                            {entry.changed && entry.changed.length > 0 && (
                                <div className="mb-4">
                                    <h3 className="text-[11px] font-semibold uppercase tracking-widest text-amber-500/80 mb-2 flex items-center gap-1.5">
                                        <RefreshCw size={11} /> Changed
                                    </h3>
                                    <ul className="space-y-1.5">
                                        {entry.changed.map((item, j) => (
                                            <li
                                                key={j}
                                                className="text-[14px] text-zinc-400 leading-relaxed"
                                            >
                                                <span className="text-zinc-600 mr-2">•</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Fixed */}
                            {entry.fixed && entry.fixed.length > 0 && (
                                <div>
                                    <h3 className="text-[11px] font-semibold uppercase tracking-widest text-blue-500/80 mb-2 flex items-center gap-1.5">
                                        <Tag size={11} /> Fixed
                                    </h3>
                                    <ul className="space-y-1.5">
                                        {entry.fixed.map((item, j) => (
                                            <li
                                                key={j}
                                                className="text-[14px] text-zinc-400 leading-relaxed"
                                            >
                                                <span className="text-zinc-600 mr-2">•</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-white/[0.04] py-5 px-6 max-w-3xl mx-auto flex justify-between text-xs text-zinc-800">
                <span>ballah code</span>
                <Link to="/tools" className="hover:text-zinc-500 transition-colors">
                    tools
                </Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
